import { Injectable, OnDestroy } from '@angular/core';
import * as dayjs from 'dayjs';
import * as timezone from 'dayjs/plugin/timezone';
import * as utc from 'dayjs/plugin/utc';
import * as localizedFormat from 'dayjs/plugin/localizedFormat';
import { Country } from '../shared/model/country';
import { Subject } from 'rxjs';
import { first } from 'rxjs/operators';
import { CountryCode } from 'libphonenumber-js/types';
import { UntilDestroy } from '@ngneat/until-destroy';

// import Hashids from 'hashids'
// hash = new Hashids('Red Squ1rr3ls and Puffins', 8, 'ABCDEFGHJKLMNPQRSTUVWXYZ1234567890');
@UntilDestroy({ checkProperties: true})
@Injectable({
  providedIn: 'root'
})
export class ConstantsService implements OnDestroy {

  constructor() {
    console.log('Starting Constants Service');
    const d: Date = new Date();
    this.TimezoneOffset = d.getTimezoneOffset();
    dayjs.extend(utc);
    dayjs.extend(timezone);
    dayjs.extend(localizedFormat);

    this.localeChanged$.pipe(first()).subscribe((tenantISO2: CountryCode) => {
      let found = false;

      for (let countryData of this.countriesData) {
        let country = new Country(countryData);

        if (!found && country.ISO2 === tenantISO2) {
          found = true;
          this.DefaultCountry = country;
          this.Countries.unshift(country);
        } else {
          this.Countries.push(country);
        }
      }
    });
  }

  public TimezoneOffset = 0;
  public AppTitle = 'ActiveSalon Cloud';

  public localeChanged$: Subject<CountryCode> = new Subject<CountryCode>();
  public Countries: Country [] = [];
  public DefaultCountry: Country;

  public MobilePhonePattern = /^((\+44(\s\(0\)\s|\s0\s|\s)?)|0)7\d{3}(\s)?\d{3}(\s)?\d{3}$/i;
  public MobilePhoneValidationMessage = 'The mobile phone number must have a format of \'07xxx xxx xxx\'';

  public TelephonePattern = /^0[123]\d{2}\s?\d{3}\s?\d{4}$|^0[123]\d{3}\s?\d{3}\s?\d{3}$|^0[123]\d{4}\s?\d{5}$/i;
  public TelephoneValidationMessage = 'The telephone number must start with 01, 02 or 03 and must have 11 digits in total.';

  public UrlPattern = /^(http: \/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i;
  public UrlValidationMessage = 'Please enter a valid URL';

  public EmailPattern = /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i;
  public EmailValidationMessage = 'Please enter a valid email address';
  public UniqueEmailValidationMessage = 'The specified email address is already registered. Please use a different email address';

  public PostcodePattern = /^([Gg][Ii][Rr] 0[Aa]{2})$|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\s?[0-9][A-Za-z]{2})$/i;
  public PostcodeValidationMessage = 'Please enter a valid UK postcode';

  public IPV4AddressPattern = /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/i;
  public IPV4AddressMessage = 'Please enter a valid IP(V4) address';

  public PasswordPattern = /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[^a-zA-Z\d\s]).{8,50}$/i;
  public PasswordMessage = 'Passwords require upper and lower case letters, digits, non-alphanumeric characters, have minimum of 8 characters and a maximum of 50 characters.';

  public UniqueNetworkIdValidationMessage = 'A resource is already assigned to the selected network id. Please use a different network id.';

  public VoucherCodePattern = /^[A-Z0-9-]{5,50}$/;
  public VoucherCodeMessage = 'The voucher code must be between 5 and 50 characters long and only contain numbers, uppercase letters, and dashes';
  public UniqueVoucherValidationMessage ="An active voucher has already been issued with the specified voucher code. Please use a different voucher code.";

  ngOnDestroy(): void {
    this.localeChanged$?.unsubscribe();
  }
  
  public static AddDefaultHeaders(e: any, ajax: any): any {
    ajax.headers = { 'CTX': 'TZ=' + (new Date()).getTimezoneOffset() + ':' };
    return ajax;
  }

  public static isToday(someDate: Date): boolean {
    const today = new Date();
    const dt = new Date(someDate);
    return this.doDatesMatch(today, dt);
  }

  public static doDatesMatch(dateA: Date, dateB: Date): boolean {
    const da = new Date(dateA);
    const db = new Date(dateB);
    return (da.getDate() === db.getDate()) &&
      (da.getMonth() === db.getMonth()) &&
      (da.getFullYear() === db.getFullYear());
  }

  public static getTimeOfDay(d: Date): number {
    if (d) {
      return (d.getHours() * 60) + d.getMinutes();
    } else {
      return 0;
    }
  }

  public static addMinutesToDate(date: Date, duration: number) {
    if (date)
      return dayjs(date).add(duration, 'minutes').toDate()
    else
      return date;
  }

  public static MakeDateTimeGMT(d: any): Date {
        
    // This code is incorrect as it
    // return dayjs(d).tz('GMT').add(dayjs(d).utcOffset() / 60, 'hour').toDate();

    if (typeof (d) !== 'string') {
      return new Date(d);
    }
    return (d.endsWith('Z')) ? dayjs(d).tz('Europe/London').toDate() : new Date(d);    
  }

  public static MakeDateTimeGMT2(d: Date): Date {
    return (d) ? dayjs(d).tz('Europe/London').toDate() : d;
  }

  public static isDaylightSavingTime(d: Date): boolean {
    let jan = new Date(d.getFullYear(), 0, 1).getTimezoneOffset();
    let jul = new Date(d.getFullYear(), 6, 1).getTimezoneOffset();
    return Math.max(jan, jul) !== d.getTimezoneOffset();
  }

  // Take date and time from different vars and combine them into a single UTC date.
  public static MakeDateTimeFromPartsAsUTC(d: Date, t: Date): Date {
    var tt = dayjs(t);
    var d2 = dayjs(d).hour(tt.hour()).minute(tt.minute())
    return dayjs(d2).toDate();
  }


  public static SecondsToString(value: number): string {

    if (value && value > 0) {
      const seconds = Math.floor((value % 60));
      const minutes = Math.floor(((value / 60) % 60));
      return `${ConstantsService.str_pad_left(minutes, '0', 2)}:${ConstantsService.str_pad_left(seconds, '0', 2)}`;
    }
    return '';
  }

  public static str_pad_left(string, pad, length): string {
    return (new Array(length + 1).join(pad) + string).slice(-length);
  }

  public RequiredFieldMessage(recordName: string, fieldName: string) {
    return `${this.capitalizeAllWords(fieldName)} is required`;
  }

  // public encodeHash(s: number): string {
  //   return this.hash.encode(s);
  // }
  // public decodeHash(s): number[] {
  //   return this.hash.decode(s);
  // }
  // public encodeHashArray(s: number[]): string {
  //   return this.hash.encode(s);
  // }
  // public decodeHashArray(s: string): number[] {
  //   return this.hash.decode(s);
  // }

  public uuidv4() {
    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
      (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> +c / 4).toString(16)
    );
  }


  public hasClass(el, className) {
    if (el.classList) {
      return el.classList.contains(className);
    }
    return !!el.className.match(new RegExp('(\\s|^)' + className + '(\\s|$)'));
  }

  public addClass(el, className) {
    if (el.classList) {
      el.classList.add(className);
    } else if (!this.hasClass(el, className)) {
      el.className += ' ' + className;
    }
  }

  public removeClass(el, className) {
    if (el.classList) {
      el.classList.remove(className)
    } else if (this.hasClass(el, className)) {
      const reg = new RegExp('(\\s|^)' + className + '(\\s|$)');
      el.className = el.className.replace(reg, ' ');
    }
  }

  public capitalizeAllWords(s) {
    const words = s.split(' ');

    for (let i = 0; i < words.length; i++) {
      words[i] = words[i][0].toUpperCase() + words[i].substr(1);
    }
    return words.join(' ');
  }

  public capitalizeFirstWord(s: string): string {
    return s[0].toUpperCase() + s.slice(1);
  }

  public getFutureDate(months: number): Date {
    const d = new Date();
    return new Date(new Date(d.setMonth(d.getMonth() + months)).setHours(0, 0, 0, 0));
  }
  // var finalTime = str_pad_left(minutes, '0', 2) + ':' + str_pad_left(seconds, '0', 2);

  public static base64ToFile(b64: string, fileName: string): File {
    const arr = b64.split(',');
    const bytes = atob(arr[arr.length - 1]);
    let n = bytes.length;
    const bits = new Uint8Array(n);

    while (n--) {
      bits[n] = bytes.charCodeAt(n);
    }

    return new File([bits], fileName, { type: b64.match(/[^:]\w+\/[\w-+\d.]+(?=;|,)/)[0].toString() });
  }

  private countriesData = [
    ['Canada', 'CA', '1', 'North America'],
    ['United States', 'US', '1', 'North America'],
    // ['Åland Islands', 'AX', '358', 'Europe'],
    ['Albania (Shqipëri)', 'AL', '355', 'Europe'],
    ['Andorra', 'AD', '376', 'Europe'],
    ['Austria (Österreich)', 'AT', '43', 'Europe'],
    ['Belarus (Беларусь)', 'BY', '375', 'Europe'],
    ['Belgium (België)', 'BE', '32', 'Europe'],
    ['Bosnia and Herzegovina (Босна и Херцеговина)', 'BA', '387', 'Europe'],
    ['Bulgaria (България)', 'BG', '359', 'Europe'],
    ['Croatia (Hrvatska)', 'HR', '385', 'Europe'],
    ['Cyprus (Κύπρος)', 'CY', '357', 'Europe'],
    ['Czech Republic (Česká republika)', 'CZ', '420', 'Europe'],
    ['Denmark (Danmark)', 'DK', '45', 'Europe'],
    ['Estonia (Eesti)', 'EE', '372', 'Europe'],
    // ['Faroe Islands (Føroyar)', 'FO', '298', 'Europe'],
    ['Finland (Suomi)', 'FI', '358', 'Europe'],
    ['France', 'FR', '33', 'Europe'],
    ['Germany (Deutschland)', 'DE', '49', 'Europe'],
    ['Gibraltar', 'GI', '350', 'Europe'],
    ['Greece (Ελλάδα)', 'GR', '30', 'Europe'],
    ['Guernsey', 'GG', '44', 'Europe'],
    ['Hungary (Magyarország)', 'HU', '36', 'Europe'],
    ['Iceland (Ísland)', 'IS', '354', 'Europe'],
    ['Ireland', 'IE', '353', 'Europe'],
    ['Isle of Man', 'IM', '44', 'Europe'],
    ['Italy (Italia)', 'IT', '39', 'Europe'],
    ['Jersey', 'JE', '44', 'Europe'],
    ['Kosovo', 'XK', '383', 'Europe'],
    ['Latvia (Latvija)', 'LV', '371', 'Europe'],
    ['Liechtenstein', 'LI', '423', 'Europe'],
    ['Lithuania (Lietuva)', 'LT', '370', 'Europe'],
    ['Luxembourg', 'LU', '352', 'Europe'],
    ['Macedonia (FYROM) (Македонија)', 'MK', '389', 'Europe'],
    ['Malta', 'MT', '356', 'Europe'],
    ['Moldova (Republica Moldova)', 'MD', '373', 'Europe'],
    ['Monaco', 'MC', '377', 'Europe'],
    ['Montenegro (Crna Gora)', 'ME', '382', 'Europe'],
    ['Netherlands (Nederland)', 'NL', '31', 'Europe'],
    ['Norway (Norge)', 'NO', '47', 'Europe'],
    ['Poland (Polska)', 'PL', '48', 'Europe'],
    ['Portugal', 'PT', '351', 'Europe'],
    ['Romania (România)', 'RO', '40', 'Europe'],
    // ['Russia (Россия)', 'RU', '7', 'Europe'],
    ['San Marino', 'SM', '378', 'Europe'],
    ['Serbia (Србија)', 'RS', '381', 'Europe'],
    ['Slovakia (Slovensko)', 'SK', '421', 'Europe'],
    ['Slovenia (Slovenija)', 'SI', '386', 'Europe'],
    ['Spain (España)', 'ES', '34', 'Europe'],
    // ['Svalbard and Jan Mayen', 'SJ', '47', 'Europe'],
    ['Sweden (Sverige)', 'SE', '46', 'Europe'],
    ['Switzerland (Schweiz)', 'CH', '41', 'Europe'],
    ['Ukraine (Україна)', 'UA', '380', 'Europe'],
    ['United Kingdom', 'GB', '44', 'Europe'],
    ['Vatican City (Città del Vaticano)', 'VA', '39', 'Europe'],
    // ['Algeria (‫الجزائر‬‎)', 'DZ', '213', 'Africa'],
    // ['Angola', 'AO', '244', 'Africa'],
    // ['Benin (Bénin)', 'BJ', '229', 'Africa'],
    // ['Botswana', 'BW', '267', 'Africa'],
    // ['Burkina Faso', 'BF', '226', 'Africa'],
    // ['Burundi (Uburundi)', 'BI', '257', 'Africa'],
    // ['Cameroon (Cameroun)', 'CM', '237', 'Africa'],
    // ['Cape Verde (Kabu Verdi)', 'CV', '238', 'Africa'],
    // ['Central African Republic (République centrafricaine)', 'CF', '236', 'Africa'],
    // ['Chad (Tchad)', 'TD', '235', 'Africa'],
    // ['Comoros (‫جزر القمر‬‎)', 'KM', '269', 'Africa'],
    // ['Congo (DRC) (Jamhuri ya Kidemokrasia ya Kongo)', 'CD', '243', 'Africa'],
    // ['Congo (Republic) (Congo-Brazzaville)', 'CG', '242', 'Africa'],
    // ['Côte d’Ivoire', 'CI', '225', 'Africa'],
    // ['Djibouti', 'DJ', '253', 'Africa'],
    // ['Egypt (‫مصر‬‎)', 'EG', '20', 'Africa'],
    // ['Equatorial Guinea (Guinea Ecuatorial)', 'GQ', '240', 'Africa'],
    // ['Eritrea', 'ER', '291', 'Africa'],
    // ['Ethiopia', 'ET', '251', 'Africa'],
    // ['Gabon', 'GA', '241', 'Africa'],
    // ['Gambia', 'GM', '220', 'Africa'],
    // ['Ghana (Gaana)', 'GH', '233', 'Africa'],
    // ['Guinea (Guinée)', 'GN', '224', 'Africa'],
    // ['Guinea-Bissau (Guiné Bissau)', 'GW', '245', 'Africa'],
    // ['Kenya', 'KE', '254', 'Africa'],
    // ['Lesotho', 'LS', '266', 'Africa'],
    // ['Liberia', 'LR', '231', 'Africa'],
    // ['Libya (‫ليبيا‬‎)', 'LY', '218', 'Africa'],
    // ['Madagascar (Madagasikara)', 'MG', '261', 'Africa'],
    // ['Malawi', 'MW', '265', 'Africa'],
    // ['Mali', 'ML', '223', 'Africa'],
    // ['Mauritania (‫موريتانيا‬‎)', 'MR', '222', 'Africa'],
    // ['Mauritius (Moris)', 'MU', '230', 'Africa'],
    // ['Mayotte', 'YT', '262', 'Africa'],
    // ['Morocco (‫المغرب‬‎)', 'MA', '212', 'Africa'],
    // ['Mozambique (Moçambique)', 'MZ', '258', 'Africa'],
    // ['Namibia (Namibië)', 'NA', '264', 'Africa'],
    // ['Niger (Nijar)', 'NE', '227', 'Africa'],
    // ['Nigeria', 'NG', '234', 'Africa'],
    // ['Réunion (La Réunion)', 'RE', '262', 'Africa'],
    // ['Rwanda', 'RW', '250', 'Africa'],
    // ['Saint Helena', 'SH', '290', 'Africa'],
    // ['São Tomé and Príncipe (São Tomé e Príncipe)', 'ST', '239', 'Africa'],
    // ['Senegal (Sénégal)', 'SN', '221', 'Africa'],
    // ['Seychelles', 'SC', '248', 'Africa'],
    // ['Sierra Leone', 'SL', '232', 'Africa'],
    // ['Somalia (Soomaaliya)', 'SO', '252', 'Africa'],
    // ['South Africa', 'ZA', '27', 'Africa'],
    // ['South Sudan (‫جنوب السودان‬‎)', 'SS', '211', 'Africa'],
    // ['Sudan (‫السودان‬‎)', 'SD', '249', 'Africa'],
    // ['Swaziland', 'SZ', '268', 'Africa'],
    // ['Tanzania', 'TZ', '255', 'Africa'],
    // ['Togo', 'TG', '228', 'Africa'],
    // ['Tunisia (‫تونس‬‎)', 'TN', '216', 'Africa'],
    // ['Uganda', 'UG', '256', 'Africa'],
    // ['Western Sahara (‫الصحراء الغربية‬‎)', 'EH', '212', 'Africa'],
    // ['Zambia', 'ZM', '260', 'Africa'],
    // ['Zimbabwe', 'ZW', '263', 'Africa'],
    // ['Afghanistan (‫افغانستان‬‎)', 'AF', '93', 'Asia'],
    // ['Armenia (Հայաստան)', 'AM', '374', 'Asia'],
    // ['Azerbaijan (Azərbaycan)', 'AZ', '994', 'Asia'],
    // ['Bahrain (‫البحرين‬‎)', 'BH', '973', 'Asia'],
    // ['Bangladesh (বাংলাদেশ)', 'BD', '880', 'Asia'],
    // ['Bhutan (འབྲུག)', 'BT', '975', 'Asia'],
    // ['British Indian Ocean Territory', 'IO', '246', 'Asia'],
    // ['Brunei', 'BN', '673', 'Asia'],
    // ['Cambodia (កម្ពុជា)', 'KH', '855', 'Asia'],
    // ['China (中国)', 'CN', '86', 'Asia'],
    // ['Christmas Island', 'CX', '61', 'Asia'],
    // ['Cocos (Keeling) Islands', 'CC', '61', 'Asia'],
    // ['Georgia (საქართველო)', 'GE', '995', 'Asia'],
    // ['Hong Kong (香港)', 'HK', '852', 'Asia'],
    // ['India (भारत)', 'IN', '91', 'Asia'],
    // ['Indonesia', 'ID', '62', 'Asia'],
    // ['Iran (‫ایران‬‎)', 'IR', '98', 'Asia'],
    // ['Iraq (‫العراق‬‎)', 'IQ', '964', 'Asia'],
    // ['Israel (‫ישראל‬‎)', 'IL', '972', 'Asia'],
    // ['Japan (日本)', 'JP', '81', 'Asia'],
    // ['Jordan (‫الأردن‬‎)', 'JO', '962', 'Asia'],
    // ['Kazakhstan (Казахстан)', 'KZ', '7', 'Asia'],
    // ['Kuwait (‫الكويت‬‎)', 'KW', '965', 'Asia'],
    // ['Kyrgyzstan (Кыргызстан)', 'KG', '996', 'Asia'],
    // ['Laos (ລາວ)', 'LA', '856', 'Asia'],
    // ['Lebanon (‫لبنان‬‎)', 'LB', '961', 'Asia'],
    // ['Macau (澳門)', 'MO', '853', 'Asia'],
    // ['Malaysia', 'MY', '60', 'Asia'],
    // ['Maldives', 'MV', '960', 'Asia'],
    // ['Mongolia (Монгол)', 'MN', '976', 'Asia'],
    // ['Myanmar (Burma) (မြန်မာ)', 'MM', '95', 'Asia'],
    // ['Nepal (नेपाल)', 'NP', '977', 'Asia'],
    // ['North Korea (조선 민주주의 인민 공화국)', 'KP', '850', 'Asia'],
    // ['Oman (‫عُمان‬‎)', 'OM', '968', 'Asia'],
    // ['Pakistan (‫پاکستان‬‎)', 'PK', '92', 'Asia'],
    // ['Palestine (‫فلسطين‬‎)', 'PS', '970', 'Asia'],
    // ['Philippines', 'PH', '63', 'Asia'],
    // ['Qatar (‫قطر‬‎)', 'QA', '974', 'Asia'],
    // ['Saudi Arabia (‫المملكة العربية السعودية‬‎)', 'SA', '966', 'Asia'],
    // ['Singapore', 'SG', '65', 'Asia'],
    // ['South Korea (대한민국)', 'KR', '82', 'Asia'],
    // ['Sri Lanka (ශ්‍රී ලංකාව)', 'LK', '94', 'Asia'],
    // ['Syria (‫سوريا‬‎)', 'SY', '963', 'Asia'],
    // ['Taiwan (台灣)', 'TW', '886', 'Asia'],
    // ['Tajikistan', 'TJ', '992', 'Asia'],
    // ['Thailand (ไทย)', 'TH', '66', 'Asia'],
    // ['Turkey (Türkiye)', 'TR', '90', 'Asia'],
    // ['Turkmenistan', 'TM', '993', 'Asia'],
    // ['United Arab Emirates (‫الإمارات العربية المتحدة‬‎)', 'AE', '971', 'Asia'],
    // ['Uzbekistan (Oʻzbekiston)', 'UZ', '998', 'Asia'],
    // ['Vietnam (Việt Nam)', 'VN', '84', 'Asia'],
    // ['Yemen (‫اليمن‬‎)', 'YE', '967', 'Asia'],
    // ['American Samoa', 'AS', '1684', 'Oceania'],
    // ['Australia', 'AU', '61', 'Oceania'],
    // ['Cook Islands', 'CK', '682', 'Oceania'],
    // ['Fiji', 'FJ', '679', 'Oceania'],
    // ['French Polynesia (Polynésie française)', 'PF', '689', 'Oceania'],
    // ['Guam', 'GU', '1671', 'Oceania'],
    // ['Kiribati', 'KI', '686', 'Oceania'],
    // ['Marshall Islands', 'MH', '692', 'Oceania'],
    // ['Micronesia', 'FM', '691', 'Oceania'],
    // ['Nauru', 'NR', '674', 'Oceania'],
    // ['New Caledonia (Nouvelle-Calédonie)', 'NC', '687', 'Oceania'],
    // ['New Zealand', 'NZ', '64', 'Oceania'],
    // ['Niue', 'NU', '683', 'Oceania'],
    // ['Norfolk Island', 'NF', '672', 'Oceania'],
    // ['Northern Mariana Islands', 'MP', '1670', 'Oceania'],
    // ['Palau', 'PW', '680', 'Oceania'],
    // ['Papua New Guinea', 'PG', '675', 'Oceania'],
    // ['Samoa', 'WS', '685', 'Oceania'],
    // ['Solomon Islands', 'SB', '677', 'Oceania'],
    // ['Timor-Leste', 'TL', '670', 'Oceania'],
    // ['Tokelau', 'TK', '690', 'Oceania'],
    // ['Tonga', 'TO', '676', 'Oceania'],
    // ['Tuvalu', 'TV', '688', 'Oceania'],
    // ['Vanuatu', 'VU', '678', 'Oceania'],
    // ['Wallis and Futuna', 'WF', '681', 'Oceania'],
    // ['Anguilla', 'AI', '1264', 'North America'],
    // ['Antigua and Barbuda', 'AG', '1268', 'North America'],
    // ['Aruba', 'AW', '297', 'North America'],
    // ['Bahamas', 'BS', '1242', 'North America'],
    // ['Barbados', 'BB', '1246', 'North America'],
    // ['Belize', 'BZ', '501', 'North America'],
    // ['Bermuda', 'BM', '1441', 'North America'],
    // ['British Virgin Islands', 'VG', '1284', 'North America'],
    // ['Caribbean Netherlands', 'BQ', '599', 'North America'],
    // ['Cayman Islands', 'KY', '1345', 'North America'],
    // ['Costa Rica', 'CR', '506', 'North America'],
    // ['Cuba', 'CU', '53', 'North America'],
    // ['Curaçao', 'CW', '599', 'North America'],
    // ['Dominica', 'DM', '1767', 'North America'],
    // ['Dominican Republic (República Dominicana)', 'DO', '1', 'North America'],
    // ['El Salvador', 'SV', '503', 'North America'],
    // ['Greenland (Kalaallit Nunaat)', 'GL', '299', 'North America'],
    // ['Grenada', 'GD', '1473', 'North America'],
    // ['Guadeloupe', 'GP', '590', 'North America'],
    // ['Guatemala', 'GT', '502', 'North America'],
    // ['Haiti', 'HT', '509', 'North America'],
    // ['Honduras', 'HN', '504', 'North America'],
    // ['Jamaica', 'JM', '1876', 'North America'],
    // ['Martinique', 'MQ', '596', 'North America'],
    // ['Mexico (México)', 'MX', '52', 'North America'],
    // ['Montserrat', 'MS', '1664', 'North America'],
    // ['Nicaragua', 'NI', '505', 'North America'],
    // ['Panama (Panamá)', 'PA', '507', 'North America'],
    // ['Puerto Rico', 'PR', '1', 'North America'],
    // ['Saint Barthélemy (Saint-Barthélemy)', 'BL', '590', 'North America'],
    // ['Saint Kitts and Nevis', 'KN', '1869', 'North America'],
    // ['Saint Lucia', 'LC', '1758', 'North America'],
    // ['Saint Martin (Saint-Martin (partie française))', 'MF', '590', 'North America'],
    // ['Saint Pierre and Miquelon (Saint-Pierre-et-Miquelon)', 'PM', '508', 'North America'],
    // ['Saint Vincent and the Grenadines', 'VC', '1784', 'North America'],
    // ['Sint Maarten', 'SX', '1721', 'North America'],
    // ['Trinidad and Tobago', 'TT', '1868', 'North America'],
    // ['Turks and Caicos Islands', 'TC', '1649', 'North America'],
    // ['U.S. Virgin Islands', 'VI', '1340', 'North America'],
    // ['Argentina', 'AR', '54', 'South America'],
    // ['Bolivia', 'BO', '591', 'South America'],
    // ['Brazil (Brasil)', 'BR', '55', 'South America'],
    // ['Chile', 'CL', '56', 'South America'],
    // ['Colombia', 'CO', '57', 'South America'],
    // ['Ecuador', 'EC', '593', 'South America'],
    // ['Falkland Islands (Islas Malvinas)', 'FK', '500', 'South America'],
    // ['French Guiana (Guyane française)', 'GF', '594', 'South America'],
    // ['Guyana', 'GY', '592', 'South America'],
    // ['Paraguay', 'PY', '595', 'South America'],
    // ['Peru (Perú)', 'PE', '51', 'South America'],
    // ['Suriname', 'SR', '597', 'South America'],
    // ['Uruguay', 'UY', '598', 'South America'],
    // ['Venezuela', 'VE', '58', 'South America'],
  ];
}
